import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { getPublishedVideos, type VideoCatalogEntry } from "../services/videoService";

type CategoryFilter = "all" | "product" | "tutorial" | "update";

export function VideoCatalogPage() {
  const { videoId } = useParams<{ videoId?: string }>();
  const navigate = useNavigate();
  const [videos, setVideos] = useState<VideoCatalogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<CategoryFilter>("all");
  const [selectedVideo, setSelectedVideo] = useState<VideoCatalogEntry | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadVideos();
  }, []);

  // Open video from URL param once videos are loaded
  useEffect(() => {
    if (videoId && videos.length > 0) {
      const match = videos.find((v) => v.youtube_video_id === videoId);
      if (match) setSelectedVideo(match);
    }
  }, [videoId, videos]);

  const openVideo = (video: VideoCatalogEntry) => {
    setSelectedVideo(video);
    navigate(`/walkthroughs/${video.youtube_video_id}`, { replace: true });
  };

  const closeVideo = () => {
    setSelectedVideo(null);
    setCopied(false);
    navigate("/walkthroughs", { replace: true });
  };

  const loadVideos = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getPublishedVideos();
      setVideos(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load videos");
    } finally {
      setIsLoading(false);
    }
  };

  const filteredVideos = filter === "all" ? videos : videos.filter((v) => v.category === filter);

  const categoryLabels: Record<CategoryFilter, string> = {
    all: "All",
    product: "Product",
    tutorial: "Tutorials",
    update: "Updates",
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Product Walkthroughs"
        subtitle="Feature demos, tutorials, and platform updates"
      />

      {/* Category Filter */}
      <div className="flex gap-2">
        {(Object.keys(categoryLabels) as CategoryFilter[]).map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setFilter(cat)}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
              filter === cat
                ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
                : "bg-surface-800/50 text-text-secondary border-surface-700/50 hover:text-text-primary hover:border-surface-600"
            }`}
          >
            {categoryLabels[cat]}
          </button>
        ))}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && filteredVideos.length === 0 && (
        <div className="text-center py-16">
          <svg
            className="w-12 h-12 mx-auto text-text-secondary/50 mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-text-secondary text-sm">No videos available yet</p>
        </div>
      )}

      {/* Video Grid */}
      {!isLoading && filteredVideos.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredVideos.map((video) => (
            <button
              key={video.id}
              type="button"
              onClick={() => openVideo(video)}
              className="rounded-xl bg-surface-800/50 border border-surface-700/50 overflow-hidden hover:border-cyan-500/30 transition-colors text-left group"
            >
              {/* Thumbnail */}
              <div className="relative aspect-video w-full overflow-hidden">
                <img
                  src={`https://img.youtube.com/vi/${video.youtube_video_id}/maxresdefault.jpg`}
                  alt={video.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    if (img.naturalWidth <= 120 && !img.dataset.fallback) {
                      img.dataset.fallback = "1";
                      img.src = `https://img.youtube.com/vi/${video.youtube_video_id}/hqdefault.jpg`;
                    }
                  }}
                  onError={(e) => {
                    const img = e.currentTarget;
                    if (!img.dataset.fallback) {
                      img.dataset.fallback = "1";
                      img.src = `https://img.youtube.com/vi/${video.youtube_video_id}/hqdefault.jpg`;
                    }
                  }}
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-black/60 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
              {/* Info */}
              <div className="p-3">
                <h3 className="text-sm font-medium text-text-primary line-clamp-2">{video.title}</h3>
                {video.description && (
                  <p className="text-xs text-text-secondary mt-1 line-clamp-2">{video.description}</p>
                )}
                <div className="mt-2">
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded ${
                      video.category === "product"
                        ? "bg-cyan-500/20 text-cyan-400"
                        : video.category === "tutorial"
                          ? "bg-emerald-500/20 text-emerald-400"
                          : "bg-amber-500/20 text-amber-400"
                    }`}
                  >
                    {video.category}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Video Player Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 z-50 flex items-start md:items-center justify-center bg-black/80 backdrop-blur-sm p-4 pt-12 md:pt-4 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-label={selectedVideo.title}
          onClick={() => closeVideo()}
          onKeyDown={(e) => { if (e.key === "Escape") closeVideo(); }}
          tabIndex={-1}
          ref={(el) => el?.focus()}
        >
          <div
            className="w-full max-w-4xl rounded-xl overflow-hidden bg-surface-900 border border-surface-700 my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-video w-full">
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.youtube_video_id}?autoplay=1&rel=0`}
                title={selectedVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
            <div className="p-4 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-medium text-text-primary">{selectedVideo.title}</h2>
                {selectedVideo.description && (
                  <p className="text-sm text-text-secondary mt-1">{selectedVideo.description}</p>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${window.location.origin}/walkthroughs/${selectedVideo.youtube_video_id}`
                    );
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className="px-3 py-1.5 text-xs rounded-lg bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors"
                >
                  {copied ? "Copied!" : "Copy Link"}
                </button>
                <button
                  type="button"
                  onClick={() => closeVideo()}
                  className="px-3 py-1.5 text-xs rounded-lg bg-surface-700 text-text-secondary hover:text-text-primary transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
